import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  Button,
  LoadingIndicator,
  Modal,
  SvgIcon,
  SvgNames,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import { useMediaQuery } from "react-responsive";
import { Card } from "../molecules/Card";
import { PpcRequiredWarningBanner } from "../molecules/PpcRequiredWarningBanner";
import { RemoteWorkSettingsModal } from "../molecules/RemoteWorkSettingsModal";
import { ScheduleAppointment } from "./ScheduleAppointment";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useSiteSurveyAppointments } from "checkout/api/siteSurvey";
import { useHqsEligibilityByProposalId } from "hooks/useHqs";
import { useHqsUrl } from "hooks/useHqs";
import { usePpcRequired } from "hooks/usePpcRequired";
import { useUsageData } from "utils/swrHooks";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

const HQS_COMPLETE_DESCRIPTION =
  "Your Fastlane Home Review is complete. We will review your responses and notify you by email.";
const HQS_COMPLETE_TITLE = "Fastlane Home Review Completed";
const HQS_DESCRIPTION =
  "Complete your Fastlane Home Review now to speed the customer's timeline to installation.";
const HQS_SUBTITLE =
  "We’ll schedule an in-person or virtual survey to measure your roof and see if your home is ready for solar. We'll need to access your attic and electrical panel to complete this part.";
const HQS_SUBTITLE_NON_SOLAR =
  "We’ll schedule an in-person or virtual survey to measure your roof and see if your home is ready for Sunrun installation. We'll need to access your attic and electrical panel to complete this part.";
const HQS_REQUIRED_SUBTITLE =
  "We need to review your home and check for certain requirements - like whether your roof is in good condition or if we need to make electrical updates - to confirm that your house is ready for solar panels. In case you need help from a technician you can schedule a site visit. Once you’ve completed the survey, the status might take several minutes to update. Please refresh this page.";
const HQS_REQUIRED_SUBTITLE_NON_SOLAR =
  "We need to review your home and check for certain requirements - like whether your roof is in good condition or if we need to make electrical updates - to confirm that your house is ready for Sunrun installation. In case you need help from a technician you can schedule a site visit. Once you’ve completed the survey, the status might take several minutes to update. Please refresh this page.";
const HQS_TITLE = "Complete Home Review Now";
const HQS_REQUIRED_TITLE = "Fastlane Home Review";
const SCHEDULE_DESCRIPTION =
  "A Site Technician will visit your home to run an on-site qualification.";
const SCHEDULE_TITLE = "Schedule a visit with a technician";

const HqsContainer = styled.div<{ isMobile?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  > .card-container {
    display: flex;
    gap: 24px;
    @media screen and (max-width: ${mobileMaxWidth}px) {
      flex-direction: column;
    }
    & > * {
      flex: 1 1 0px;
    }
  }

  ${({ isMobile }) =>
    isMobile &&
    `
    padding-bottom: 64px;
  `}
`;

const SettingsWrapper = styled.div<{ isMobile?: boolean }>`
  position: absolute;
  top: ${({ isMobile }) => (isMobile ? "-41px" : "-57px")};
  right: 0;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const TaskContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HqsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;
  padding: 24px;
  & > .info {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 400px;
  font-size: ${tokens.FONT_SIZE_3}px;
  line-height: ${tokens.FONT_SIZE_4}px;
  color: ${tokens.BRAND_HERO_BLUE};
  letter-spacing: -0.4px;
  font-weight: 400;

  svg {
    /* Need !important to override inline styles */
    height: 40px !important;
    width: 40px !important;
  }
`;

const Disclaimer = styled.p`
  text-align: center;
  font-weight: 400;
  font-size: ${tokens.FONT_SIZE_0}px;
  color: ${tokens.TINTS_OFF_WHITE_20};
  margin-top: 12px;
`;

const HqsHelpButton = styled(Button)<{ isMobile?: boolean }>`
  width: fit-content;
  padding: 0;
  ${({ isMobile }) =>
    isMobile &&
    `
    display: flex;
    flex-direction: column;
  `}
`;

const HqsHelpLink = styled.a`
  color: ${tokens.BRAND_HERO_BLUE};
  text-decoration: underline;
  font-size: ${tokens.FONT_SIZE_1}px;
  font-weight: 500;
`;

const HqsHelpText = styled(Typography)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 640px;
`;

const SiteSurveyTaskView: React.FC = () => {
  const [showHqsHelp, setShowHqsHelp] = useState<boolean>(false);
  const [showAppointmentScheduler, setShowAppointmentScheduler] =
    useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [overrideScheduleAppointment, setOverrideScheduleAppointment] =
    useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });

  const {
    prospectId,
    proposalId,
    currentTask,
    setLoadingOverride,
    handleTitleOverride,
    isHqsRequired,
    salesDivision,
    isNonSolarOffer,
    tasks,
  } = useCheckoutContext();

  const isDesignPreferencesComplete = Boolean(
    tasks.find((t) => t.id === "designPreferences")?.isComplete
  );

  const {
    data: isEligibleForHqsData,
    loading: isEligibleForHqsLoading,
    error: hqsError,
    mutate: hqsRetry,
  } = useHqsEligibilityByProposalId(proposalId);
  const {
    siteSurveyAppointment: isAppointmentScheduled,
    siteSurveyAppointmentsLoading: isAppointmentScheduledLoading,
  } = useSiteSurveyAppointments(prospectId);
  const hqsUrl = useHqsUrl({ prospectId });
  const { prospect } = useUsageData(prospectId);

  const homeReviewLoading =
    isEligibleForHqsLoading || isAppointmentScheduledLoading;

  useEffect(() => {
    setLoadingOverride(homeReviewLoading && !showAppointmentScheduler);
  }, [homeReviewLoading, setLoadingOverride, showAppointmentScheduler]);

  useEffect(() => {
    if (!!hqsError !== showErrorModal) {
      setShowErrorModal(!!hqsError);
    }
  }, [hqsError, showErrorModal]);

  useEffect(() => {
    let title = undefined;

    if (isHqsRequired) {
      title = "Home Review";
    }

    if (!isEligibleForHqsData || showAppointmentScheduler) {
      title = "Schedule On-Site Home Review";
    }

    handleTitleOverride(title);
  }, [
    handleTitleOverride,
    isEligibleForHqsData,
    isHqsRequired,
    showAppointmentScheduler,
  ]);

  const isTaskComplete = currentTask?.isComplete ?? false;
  const { isPpcRequired } = usePpcRequired({
    lookupId: prospectId,
    enabled: isTaskComplete,
  });
  const showPpcRequiredWarning = isTaskComplete && isPpcRequired === true;
  const isNIS = salesDivision === "NIS";

  const PhysicalHomeReview = () => {
    return (
      <HqsContainer>
        <Typography>
          {isNonSolarOffer ? HQS_SUBTITLE_NON_SOLAR : HQS_SUBTITLE}
        </Typography>
        <div className="card-container">
          <PhysicalHomeReviewCard />
        </div>
      </HqsContainer>
    );
  };

  const PhysicalHomeReviewCard = () => {
    return (
      <HqsCard>
        <div className="info">
          <SvgIcon name={SvgNames.HardHat} height={56} />
          <Typography size="large" fontWeight="medium">
            {SCHEDULE_TITLE}
          </Typography>
          <Typography>{SCHEDULE_DESCRIPTION}</Typography>
        </div>
        <div className="actions">
          <Button
            data-testid="site-survey-schedule-appointment"
            size="sm"
            color="secondary"
            onClick={() => setShowAppointmentScheduler(true)}
          >
            Schedule
          </Button>
        </div>
      </HqsCard>
    );
  };

  const HqsTask: React.FC = useMemo(
    () => () => {
      const subtitle = isHqsRequired
        ? isNonSolarOffer
          ? HQS_REQUIRED_SUBTITLE_NON_SOLAR
          : HQS_REQUIRED_SUBTITLE
        : isNonSolarOffer
        ? HQS_SUBTITLE_NON_SOLAR
        : HQS_SUBTITLE;

      return (
        <HqsContainer isMobile={isMobile}>
          {showPpcRequiredWarning && <PpcRequiredWarningBanner />}
          <SettingsWrapper
            role="button"
            onClick={() => setShowSettingsModal(true)}
            isMobile={isMobile}
          >
            <SvgIcon name={SvgNames.Settings} width={20} height={20} />
          </SettingsWrapper>
          <Typography>{subtitle}</Typography>
          <div className="card-container">
            <HqsCard>
              {!isTaskComplete && (
                <>
                  <div className="info">
                    <SvgIcon name={SvgNames.Clipboard} width={56} height={56} />
                    <Typography size="large" fontWeight="medium">
                      {isHqsRequired ? HQS_REQUIRED_TITLE : HQS_TITLE}
                    </Typography>
                    <Typography>{HQS_DESCRIPTION}</Typography>
                  </div>
                  <div className="actions">
                    <Button
                      data-testid="site-survey-hqs-open"
                      size="sm"
                      color={isHqsRequired ? "primary" : "secondary"}
                      onClick={() => window.open(hqsUrl, "_blank")}
                    >
                      {isHqsRequired ? "Fastlane Home Review" : "Start"}
                    </Button>
                  </div>
                </>
              )}
              {isTaskComplete && (
                <div className="info" data-testid="site-survey-hqs-complete">
                  <SvgIcon
                    name={SvgNames.CheckEnclosed}
                    width={56}
                    height={56}
                    color={tokens.SUCCESS_50}
                  />
                  <Typography size="large" fontWeight="medium">
                    {HQS_COMPLETE_TITLE}
                  </Typography>
                  <Typography>{HQS_COMPLETE_DESCRIPTION}</Typography>
                </div>
              )}
            </HqsCard>
            {(isTaskComplete ||
              !isEligibleForHqsData ||
              isNIS ||
              overrideScheduleAppointment) && <PhysicalHomeReviewCard />}
          </div>
          {isHqsRequired && (
            <>
              <HqsHelpButton
                isMobile={isMobile}
                onClick={() => setShowHqsHelp((prev) => !prev)}
                color="tertiary"
                data-testid="home-review-troubleshoot"
                iconRight={
                  <SvgIcon
                    name={
                      showHqsHelp
                        ? SvgNames.ChevronDown
                        : isMobile
                        ? SvgNames.ChevronDown
                        : SvgNames.ChevronRight
                    }
                    color={tokens.BLACK}
                    style={{ width: "16px", height: "16px" }}
                  />
                }
              >
                Are you having trouble completing the home review?
              </HqsHelpButton>
              {showHqsHelp && (
                <HqsHelpText color={tokens.TINTS_OFF_WHITE_20}>
                  <span>
                    If your Fastlane submission is disqualified, you will
                    automatically receive a link to schedule a technician site
                    survey via text and email.
                  </span>
                  <span>
                    If you (1) don’t receive the self-scheduling link or (2) are
                    having issues scheduling the appointment, please call Sunrun
                    Sales Support{" "}
                    <HqsHelpLink href="tel:888-840-7180">
                      888-840-7180
                    </HqsHelpLink>
                  </span>
                </HqsHelpText>
              )}
            </>
          )}
          {showErrorModal && (
            <Modal
              testId="checkout-site-survey-error-modal"
              hideClose
              type="error"
              title="There was an issue verifying your Fastlane eligibility"
              primaryButton={{
                testId: "modal-home-review-retry",
                text: "Retry",
                onClick: () => hqsRetry(),
              }}
              secondaryButton={{
                testId: "modal-home-review-schedule-appointment",
                text: "Schedule a visit",
                onClick: () => {
                  setShowErrorModal(false);
                  setShowAppointmentScheduler(true);
                },
              }}
              onClose={() => {
                setShowErrorModal(false);
                setShowAppointmentScheduler(true);
              }}
            >
              <Typography>
                Something went wrong verifying your eligibility for Fastlane
                Home Review. Please retry, or schedule an on-site visit with a
                technician.
              </Typography>
            </Modal>
          )}
          {showSettingsModal && (
            <RemoteWorkSettingsModal
              initialIsRemoteWorking={overrideScheduleAppointment}
              isDesignPreferencesComplete={isDesignPreferencesComplete}
              onClose={() => setShowSettingsModal(false)}
              onSave={(isRemoteWorking) => {
                setShowSettingsModal(false);
                setOverrideScheduleAppointment(isRemoteWorking);
              }}
              customerLatitude={prospect?.latitude}
              customerLongitude={prospect?.longitude}
            />
          )}
        </HqsContainer>
      );
    },
    [
      isHqsRequired,
      isNonSolarOffer,
      isMobile,
      isTaskComplete,
      isEligibleForHqsData,
      isNIS,
      overrideScheduleAppointment,
      showHqsHelp,
      showErrorModal,
      hqsRetry,
      showSettingsModal,
      prospect?.latitude,
      prospect?.longitude,
      hqsUrl,
      isDesignPreferencesComplete,
      showPpcRequiredWarning,
    ]
  );

  if (showAppointmentScheduler) {
    return (
      <TaskContentWrapper>
        {showPpcRequiredWarning && <PpcRequiredWarningBanner />}
        <ScheduleAppointment />
      </TaskContentWrapper>
    );
  }
  if (
    isTaskComplete &&
    !isAppointmentScheduledLoading &&
    isAppointmentScheduled
  ) {
    return (
      <TaskContentWrapper>
        {showPpcRequiredWarning && <PpcRequiredWarningBanner />}
        <ScheduleAppointment />
      </TaskContentWrapper>
    );
  }

  if (homeReviewLoading) {
    return (
      <LoadingWrapper>
        <LoadingIndicator color="black" message="Loading Home Review" />
        <Disclaimer>It may take several minutes to update.</Disclaimer>
      </LoadingWrapper>
    );
  }
  return (
    <>
      {(isEligibleForHqsData || showErrorModal) && <HqsTask />}
      {(!isEligibleForHqsData || showErrorModal) && isNIS && (
        <PhysicalHomeReview />
      )}
      {!isEligibleForHqsData && !showErrorModal && <ScheduleAppointment />}
    </>
  );
};

export {
  HQS_COMPLETE_DESCRIPTION,
  HQS_COMPLETE_TITLE,
  HQS_DESCRIPTION,
  HQS_SUBTITLE,
  HQS_TITLE,
  SCHEDULE_DESCRIPTION,
  SCHEDULE_TITLE,
  SiteSurveyTaskView,
};
